import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledInputSelect } from '@components/controlled-input-select';
import { REQUIRED_TEXT_ERROR } from '@components/dynamic-input-fields/constants';
import { InputSelectAddons } from '@components/input-select-addons';
import { InputSelectAddonsMode } from '@components/input-select-addons/input-select-addons.type';
import { InputSelectAdmission } from '@components/input-select-admission';
import { Admission } from '@domain/admission';
import { EventListItem } from '@domain/event';
import { AdmissionResponse } from '@hooks/admissions/use-get-admissions.type';
import { formatDateName } from '@utils/date-time-format/format-date-name';

import { filterByRole } from '../utils/filter-by-role';
import { shouldFilterOfferIfIsSiae } from '../utils/filter-offer-if-siae';

import { SelectTypeFormProps } from './select-type-form.type';

export const SelectTypeForm = ({
  eventListFiltered,
  admissionList,
  addOnsList,
  showAddons,
  channelList,
  offer,
  selectedAddons,
  setSelectedAddons,
  isSiaeImportLimited,
  timezone,
  role,
  parentRef,
}: SelectTypeFormProps) => {
  const { t, i18n } = useTranslation();
  const { watch, setValue } = useFormContext();
  const eventId = watch('eventId');
  const channelId = watch('channelId');

  const filterCondition = (selectedOffer: Admission) => shouldFilterOfferIfIsSiae(selectedOffer, isSiaeImportLimited);
  const filterOfferByRole = (selectedOffer: Admission) =>
    filterByRole({ role: role?.name, isHidden: selectedOffer.settings.isHidden });

  const filteredAdmissionList = {
    bottleService:
      admissionList?.bottleService.filter((bottle) => filterCondition(bottle) && filterOfferByRole(bottle)) ?? [],
    guestList: admissionList?.guestList.filter((guest) => filterCondition(guest) && filterOfferByRole(guest)) ?? [],
    ticket: admissionList?.ticket.filter((ticket) => filterCondition(ticket) && filterOfferByRole(ticket)) ?? [],
  };

  const handleEventSelectorChange = () => {
    if (
      filteredAdmissionList.bottleService.length === 0 &&
      filteredAdmissionList.ticket.length === 0 &&
      filteredAdmissionList.guestList.length === 1
    ) {
      setValue('offerId', filteredAdmissionList.guestList[0].id, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setValue('addOns', [], { shouldDirty: true, shouldTouch: true, shouldValidate: true });
      setSelectedAddons([]);
    } else {
      setValue('offerId', '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }
  };
  const handleOfferChange = () => {
    const isDefaultChannelOnList = channelList.some((channel) => channel.id === channelId);
    setValue('addOns', [], { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    setValue('channelId', isDefaultChannelOnList ? channelId : '', {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    setSelectedAddons([]);
  };

  return (
    <>
      {eventListFiltered && (
        <ControlledInputSelect
          fieldRegisterName='eventId'
          options={eventListFiltered?.flat().map((e: EventListItem) => ({
            ...e,
            name: formatDateName(e.name, e.startingTime, timezone, i18n.language),
          }))}
          rules={{ required: { message: t(REQUIRED_TEXT_ERROR), value: true } }}
          onChange={handleEventSelectorChange}
          label={t('offers:selectAnEvent')}
        />
      )}
      {admissionList && (
        <InputSelectAdmission
          admissionList={filteredAdmissionList as AdmissionResponse}
          eventId={eventId}
          selectedOffer={offer}
          onChange={handleOfferChange}
        />
      )}
      {showAddons && offer && (
        <InputSelectAddons
          parentRef={parentRef}
          addOnsList={addOnsList ?? []}
          selectedAddons={selectedAddons}
          required={offer.hasMandatoryAddOns}
          mode={offer.addOnChoiceMode as InputSelectAddonsMode}
          setSelectedAddons={setSelectedAddons}
        />
      )}
      {offer && (
        <ControlledInputSelect
          fieldRegisterName='channelId'
          rules={{ required: { message: t(REQUIRED_TEXT_ERROR), value: true } }}
          options={channelList}
          label={t('invitations:processModalChannelSelectorTitle')}
        />
      )}
    </>
  );
};
